import { AfterViewInit, Component, DestroyRef, forwardRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { QuillService } from 'app/core/services/quill.service';
import Quill from 'quill';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { QuillModule } from 'ngx-quill';
import { ImageService } from 'app/core/services/image.service';

import ImageResize from 'quill-image-resize-module-v2';
Quill.register('modules/imageResize', ImageResize);

// Custom Image blot to preserve style and width attributes
// By default Quill will filter out style and width attributes
// Used in dangerouslyPasteHTML to preserve image attributes
const BaseImage: any = Quill.import('formats/image');
class CustomImage extends BaseImage {
  static formats(domNode: HTMLElement) {
    const format = super.formats(domNode) || {};
    format.style = domNode.getAttribute('style') || undefined;
    format.width = domNode.getAttribute('width') || undefined;
    return format;
  }

  format(name: string, value: any) {
    if (name === 'style') {
      if (value) {
        this.domNode.setAttribute('style', value);
      } else {
        this.domNode.removeAttribute('style');
      }
    } else if (name === 'width') {
      if (value) {
        this.domNode.setAttribute('width', value);
      } else {
        this.domNode.removeAttribute('width');
      }
    } else {
      super.format(name, value);
    }
  }

  static create(value: any) {
    let domNode = super.create(value);
    if (typeof value === 'object' && value.image) {
      domNode.setAttribute('src', value.image);
      if (value.style) domNode.setAttribute('style', value.style);
      if (value.width) domNode.setAttribute('width', value.width);
    } else {
      domNode.setAttribute('src', value);
    }
    return domNode;
  }

  static value(domNode: HTMLElement) {
    return {
      image: domNode.getAttribute('src'),
      style: domNode.getAttribute('style'),
      width: domNode.getAttribute('width')
    };
  }
}
Quill.register('formats/image', CustomImage, true);

@Component({
  standalone: true,
  imports: [CommonModule, QuillModule],
  selector: 'app-quill-custom',
  templateUrl: './quill-custom.component.html',
  styleUrls: ['./quill-custom.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => QuillCustomComponent),
    multi: true,
  }]
})
export class QuillCustomComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  destroyRef = inject(DestroyRef);
  @Input() limit: number;
  @Input() options: any = {
    showToolbarList: true,
    size: 'lg',
    savingId: null,
    disableEditAndInsertEdited: false,
    showImage: false
  };
  @Input()
  get value(): any {
    return this.controlName.value;
  }
  set value(value: any) {
    this.controlName.setValue(value);
    this.stateChanges.next();
  }
  @Input() public readonly: boolean = false;

  private onChangeCallback = (value: any) => {};
  private onTouchedCallback = () => {};

  public quillClasses: any = {
    'quill-editor--sm': false
  };
  public modules: any = {
    clipboard: true,
    toolbar: [
      ['bold', 'italic', 'underline', 'link'],
    ]
  };
  public quillInstance;
  public quillInstanceInitialized = false;
  public quillSettedContent:boolean = false;
  public quillLength:number = 1;
  public input:any 
  public removeInputError

  controlName = new UntypedFormControl();

  public stateChanges = new Subject<void>();

  constructor(
    public quillService: QuillService,
    private imageService: ImageService
  ) {}

  ngOnInit(): void {
    if (this.options.showToolbarList === true) {
      this.modules.toolbar.push([
        { 'list': 'ordered'}, { 'list': 'bullet' }
      ])
    }
    if (this.options.showImage) {
      this.modules.toolbar.push(['image']);
      this.modules.imageResize = { displaySize: true };
    }
    if (this.options.size === 'sm') {
      this.quillClasses['quill-editor--sm'] = true;
    }
    if (this.options.disableEditAndInsertEdited) {
      this.modules.toolbar = [];
    }
  }

  ngAfterViewInit(): void {
    this.controlName.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      if (this.onChangeCallback) {
        const reg = new RegExp(String.fromCharCode(160), 'g');
        const onChangeCallbackValue = value ? 
          value.replaceAll(/<ol>(<li data-list="bullet">.*?<\/li>+)<\/ol>/g,"<ul>$1</ul>")
               .replaceAll(reg, ' ') // replace non-breaking space with space
          : value; 
        this.onChangeCallback(onChangeCallbackValue)
      }
    });
  }

  registerOnChange(fn: any) { 
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }
  writeValue(value: string) {
    this.controlName.setValue(value, { emitEvent: false });
    if (this.quillInstance) {
      this.quillSettedContent = true;

      const html = this.controlName.value || '';
      this.quillInstance.clipboard.dangerouslyPasteHTML(html);
    }
  }

  getEditorInstance(quill){
    // Toolbar and Link/Image Handlers
    const self = this;
    let tooltip = quill.theme.tooltip;
    this.input = tooltip.root.querySelector("input[data-link]");
    let root = tooltip.root;
    this.input.dataset.link = 'https://paybee.io or mailto:';

    this.removeInputError = function(){
      let error = root.querySelector(".quillError")
        if(error){
            error.remove()
            root.style.height = '40px'
        }
    }

    this.input.addEventListener('input',this.removeInputError)

    let errorSpan = document.createElement('span')
    errorSpan.className = 'quillError'
    errorSpan.innerHTML = 'Incorrect link. Use full url including <b>https://</b> or <b>mailto:</b>'

    quill.getModule('toolbar').addHandler('link', function() {
        tooltip.edit('link','')
        const containerRect = quill.container.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const maxLeft = containerRect.width - rootRect.width;

        self.input.style.setProperty('padding-top', '0', 'important');
        root.style.setProperty('font-size', '16px', 'important')
        const left = parseInt(root.style.left, 10);
        if(left < 0){
          root.style.left = '0'
        } else if (left > maxLeft){
          root.style.left = `${maxLeft}px`;
        }

        let error = root.querySelector(".quillError")
        if(error){
            error.remove()
            root.style.height = '40px'
        }
    });

    if (this.options.showImage) {
      quill.getModule('toolbar').addHandler('image', () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = () => {
          const file = input.files[0];
          if (file) {
            this.imageService.uploadImageByFile(file)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((response: any) => {
                const imageUrl = response.imageUrl;
                const range = quill.getSelection();
                if (range) {
                  quill.insertEmbed(range.index, 'image', imageUrl, 'user');
                  quill.setSelection(range.index + 1);
                  this.controlName.setValue(quill.root.innerHTML, { emitEvent: true });
                }
              });
          }
        };
      });
    }
    // Link Validation
    var tooltipSave = quill.theme.tooltip.save;
    quill.theme.tooltip.save = function () {
      var url = this.textbox.value;
      let urlSplit = url.split('');
      let urlRegex = null;
      if (urlSplit[0] == 'm') {
        urlRegex = /^mailto:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\?((cc|bcc|subject|body)=[^&]*(&(?:cc|bcc|subject|body)=[^&]*)*))?$/;
      } else {
        urlRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
      }
      if (urlRegex.test(url)) {
        tooltipSave.call(this);
      } else {
        root.style.height = '60px';
        root.append(errorSpan);
      }
    };

    const html = this.controlName.value || '';
    quill.clipboard.dangerouslyPasteHTML(html);

    this.quillInstance = quill;
    this.quillInstanceInitialized = true;
    this.quillSettedContent = true;
    if (this.options.savingId) {
      this.quillService.quillInstanceMap.set(this.options.savingId, quill);
    }
  }

  onEditorChanged(quill) {
    if (!this.quillInstance) {
      return;
    }

    if (this.quillSettedContent === true) {
      this.quillSettedContent = false;
      return;
    }

    const selection = this.quillInstance.getSelection();
    if (selection && this.options.savingId) {
      this.quillService.selectionMap.set(this.options.savingId, selection);
    }
    this.quillLength = this.quillInstance.getLength();
    const delta = this.quillInstance.getContents();
    const hasContent = delta.ops.some(op =>
      (typeof op.insert === 'string' && op.insert.trim().length > 0) ||
      (typeof op.insert === 'object' && op.insert.image)
    );

    
    if (this.quillLength > this.limit) {
      this.quillInstance.deleteText(this.limit, this.quillLength);
    }

    if (hasContent) {
      if (this.controlName.value !== this.quillInstance.root.innerHTML) {
        let inputString = this.quillInstance.root.innerHTML;
        var modifiedString = inputString.replace(/<p><br><\/p>/g, '<br>');
        this.controlName.setValue(modifiedString, {
          emitEvent: this.quillInstanceInitialized
        });
      }
    } else {
      this.controlName.setValue('', {
        emitEvent: this.quillInstanceInitialized
      });
    }

    if(quill.event == 'text-change'){
      let opsLength = quill.content.ops.length
      let oldOpsLength = quill.oldDelta.ops.length
      let content = quill.content.ops
      if(opsLength !== oldOpsLength){
        let cleanContent = this.quillService.formatText(content, this.options.disableEditAndInsertEdited)
        this.quillSettedContent = true
        this.quillInstance.setContents(cleanContent)

        if (selection) {
          this.quillInstance.setSelection(selection);
          this.quillService.selectionMap.set(this.options.savingId, selection);
        }
      }
    }
    if (this.options.savingId && this.options.savingId) {
      this.quillService.quillInstanceMap.set(this.options.savingId, this.quillInstance);
    }
  }

  ngOnDestroy(): void {
    if(this.input) {
      this.input.removeEventListener('input', this.removeInputError);
    }
    this.stateChanges.complete();
  }
}